import { useEffect, useMemo, useState } from 'react'
import useAxios from '../../../../hooks/useAxios'
import { parseFilterValuesToParams } from '../../../../pages/BaseOverview/BaseOverview'
import { TimeFilterType } from '../BaseTimeOverview'
import { DailyTotalDto } from './timeCalendarHelper'

type DailyTotalsProps = {
    loading: boolean
    // Keyed by ISO date; days without written time are absent.
    totals: Map<string, DailyTotalDto>
}

/**
 * Loads the written time per day for the calendar window. All overview filters apply, except the date filter:
 * the calendar supplies its own from/to because it always shows whole weeks.
 */
export default function useDailyTotals(filterValues: TimeFilterType, from: string, to: string): DailyTotalsProps {
    const axios = useAxios()
    const [loading, setLoading] = useState<boolean>(true)
    const [days, setDays] = useState<DailyTotalDto[]>([])

    const params = useMemo(
        () => ({ ...parseFilterValuesToParams({ ...filterValues, from, to }), start: undefined, size: undefined }),
        [filterValues, from, to],
    )

    useEffect(() => {
        let cancelled = false
        setLoading(true)

        axios
            .get('/time/daily-totals', { params })
            .then(response => {
                if (!cancelled) {
                    setDays(response.data ?? [])
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDays([])
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [params])

    const totals = useMemo(() => new Map(days.map(day => [day.date, day])), [days])

    return { loading, totals }
}
